import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/services/Common-function/common.service';

interface CountdownPart {
  value: string;
  label: string;
}

/**
 * Diwali sale panel for the home hero (shared): "BEAT22 DIWALI SALE" title, sale countdown
 * ("Sale Starts In" before start_at, "Sale Ends In" until end_at), the sale period and a row of sale tracks.
 * The countdown timer runs outside Angular and only this component is re-rendered.
 * Usage: <ipm-diwali-hero-panel [tracks]="beats"></ipm-diwali-hero-panel>
 */
@Component({
  selector: 'ipm-diwali-hero-panel',
  templateUrl: './diwali-hero-panel.component.html',
  styleUrls: ['./diwali-hero-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiwaliHeroPanelComponent implements OnInit, OnChanges, OnDestroy {
  /** Beats shown in "Tracks by our producers" (cover_picture, title, producer, serial_number, sale_info) */
  @Input() tracks: Array<any> = [];
  /** Cover image urls shown in the row (used instead of `tracks` when given) */
  @Input() artworks: Array<string> = [];
  @Input() tagline: string = "Up to 50% off on beats from India's top producers";

  @ViewChild('scroller') scroller: ElementRef<HTMLElement>;

  /** Tracks repeated until the row is long enough to scroll left and right */
  displayTracks: Array<any> = [];

  startAt: string | null = null;
  endAt: string | null = null;
  label: string = '';
  parts: Array<CountdownPart> = [];
  /** Sale is running (now between start_at and end_at): reversed countdown colours, anar on the last letter of DIWALI */
  isLive: boolean = false;
  /** Anar (flower-pot firecracker) sparks: fixed spread, height and timing (css animation, no timers) */
  readonly anarSparks: Array<{ dx: number; dy: number; delay: number; duration: number; size: number; color: string }> = this.buildAnarSparks();

  private timer: ReturnType<typeof setInterval> | null = null;
  private frameId = 0;
  private lastFrameAt = 0;
  private scrollPosition = 0;
  private isPaused = false;
  /** Auto scroll speed in px per second */
  private readonly autoScrollSpeed = 28;
  private saleSubscription: Subscription | null = null;

  constructor(
    public commonService: CommonService,
    private router: Router,
    private changeDetector: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  ngOnChanges(): void {
    const list: Array<any> = this.artworks && this.artworks.length > 0
      ? this.artworks.map((url: string) => ({ cover_picture: url }))
      : (this.tracks || []).filter(Boolean);
    // one "unit" is the list repeated until it is wide enough; two identical units make the auto scroll seamless
    const unit: Array<any> = [];
    if (list.length > 0) {
      while (unit.length < Math.max(6, list.length)) {
        unit.push(...list);
      }
    }
    this.displayTracks = [...unit, ...unit];
  }

  /** Scroll the tracks row by about four cards (the row loops, so there is no end) */
  scrollTracks(direction: 1 | -1): void {
    const element = this.scroller?.nativeElement;
    if (!element) {
      return;
    }
    const step = Math.max(element.clientWidth * 0.8, 86);
    const half = element.scrollWidth / 2;
    if (direction === -1 && element.scrollLeft < step) {
      element.scrollLeft += half; // jump one unit forward (identical content), then scroll back smoothly
    }
    element.scrollBy({ left: direction * step, behavior: 'smooth' });
    window.setTimeout(() => this.pauseAutoScroll(this.isPaused), 600);
  }

  ngOnInit(): void {
    this.saleSubscription = this.commonService.platformSaleData.subscribe(() => {
      const sale = this.commonService.getActivePlatformSale();
      this.startAt = sale?.start_at || null;
      this.endAt = sale?.end_at || null;
      this.tick();
    });
    this.ngZone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.tick(), 1000);
      if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        this.frameId = requestAnimationFrame(this.autoScroll);
      }
    });
  }

  /** Pause the auto scroll while the pointer is over the row or it is being dragged */
  pauseAutoScroll(paused: boolean): void {
    this.isPaused = paused;
    if (!paused) {
      this.scrollPosition = this.scroller?.nativeElement.scrollLeft || 0;
    }
  }

  private readonly autoScroll = (time: number): void => {
    const element = this.scroller?.nativeElement;
    const step = this.lastFrameAt ? Math.min((time - this.lastFrameAt) / 1000, 0.1) : 0;
    this.lastFrameAt = time;
    if (element && !this.isPaused && element.scrollWidth > element.clientWidth) {
      this.scrollPosition += this.autoScrollSpeed * step;
      const half = element.scrollWidth / 2;
      if (this.scrollPosition >= half) {
        this.scrollPosition -= half;
      }
      element.scrollLeft = this.scrollPosition;
    }
    this.frameId = requestAnimationFrame(this.autoScroll);
  };

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frameId);
    this.saleSubscription?.unsubscribe();
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  goToBeat(beat: any): void {
    if (beat?.serial_number) {
      this.router.navigate(['/bt-' + beat.serial_number]);
    }
  }

  trackAbbr(beat: any): string {
    return (beat?.title || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase();
  }

  trackById(index: number): number {
    return index;
  }

  private tick(): void {
    const now = Date.now();
    const start = this.startAt ? new Date(this.startAt).getTime() : NaN;
    const end = this.endAt ? new Date(this.endAt).getTime() : NaN;
    let target = NaN;
    if (now < start) {
      this.label = 'Sale Starts In';
      target = start;
    } else if (now <= end) {
      this.label = 'Sale Ends In';
      target = end;
    } else {
      this.label = '';
    }
    this.isLive = this.label === 'Sale Ends In';
    this.parts = isNaN(target) ? [] : this.toParts(target - now);
    this.changeDetector.detectChanges();
  }

  private toParts(milliseconds: number): Array<CountdownPart> {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const pad = (value: number) => String(value).padStart(2, '0');
    return [
      { value: pad(Math.floor(totalSeconds / 86400)), label: 'Days' },
      { value: pad(Math.floor((totalSeconds % 86400) / 3600)), label: 'Hours' },
      { value: pad(Math.floor((totalSeconds % 3600) / 60)), label: 'Mins' },
      { value: pad(totalSeconds % 60), label: 'Secs' },
    ];
  }

  private buildAnarSparks() {
    const colors = ['#FDE68A', '#FBBF24', '#F59E0B', '#F97316', '#FFFFFF'];
    // deterministic spread: sparks fan out both ways, rise 40-90px and fall back down
    return Array.from({ length: 16 }, (_, i) => ({
      dx: ((i % 2 === 0 ? 1 : -1) * (6 + ((i * 7) % 28))),
      dy: -(40 + ((i * 13) % 50)),
      delay: (i * 0.11) % 1.4,
      duration: 1 + ((i * 3) % 6) / 10,
      size: 2 + (i % 3),
      color: colors[i % colors.length],
    }));
  }
}
